import React, { useState, useEffect } from "react";
import {
  Box, Typography, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import PaginationControls from "../../components/Pagination/PaginationControls";

const PAGE_SIZE = 20;

const fmtDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d)) return "-";
  const dd   = String(d.getDate()).padStart(2, "0");
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh   = String(d.getHours()).padStart(2, "0");
  const min  = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} || ${hh}:${min}`;
};

const TABS = ["All", "Date-wise", "Cancel Records"];

export default function AutopayRecords() {
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem("autopayTab") || "All"
  );

  // All tab
  const [allData, setAllData]       = useState([]);
  const [allTotal, setAllTotal]     = useState(0);
  const [allPage, setAllPage]       = useState(1);
  const [allLoading, setAllLoading] = useState(false);

  // Date-wise tab
  const [dateData, setDateData]       = useState([]);
  const [dateTotal, setDateTotal]     = useState(0);
  const [datePage, setDatePage]       = useState(1);
  const [dateLoading, setDateLoading] = useState(false);

  // Cancel Records tab
  const [cancelData, setCancelData]       = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchAll = async (page = 1) => {
    setAllLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: page === 1 ? "/payment/autopay-payment-record/" : `/payment/autopay-payment-record/?page=${page}`,
      });
      setAllData(res.data?.results?.data || res.data?.results || res.data?.data || []);
      setAllTotal(res.data?.count || res.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to fetch records");
    } finally {
      setAllLoading(false);
    }
  };

  const fetchDatewise = async (page = 1) => {
    setDateLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: page === 1 ? "/payment/datewise-autopayment/" : `/payment/datewise-autopayment/?page=${page}`,
      });
      setDateData(res.data?.results?.data || res.data?.results || res.data?.data || []);
      setDateTotal(
        res.data?.count || res.data?.total ||
        res.data?.results?.count || res.data?.results?.total || 0
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to fetch date-wise data");
    } finally {
      setDateLoading(false);
    }
  };

  const fetchCancelRecords = async () => {
    setCancelLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: "/subscription/cancel-subscription-records/" });
      setCancelData(res.data?.response || res.data?.data || res.data?.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to fetch cancel records");
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => { fetchAll(allPage); }, [allPage]); // eslint-disable-line
  useEffect(() => {
    if (activeTab === "Date-wise") fetchDatewise(datePage);
  }, [activeTab, datePage]); // eslint-disable-line
  useEffect(() => {
    if (activeTab === "Cancel Records" && cancelData.length === 0) fetchCancelRecords();
  }, [activeTab]); // eslint-disable-line

  const loading =
    activeTab === "All" ? allLoading :
    activeTab === "Date-wise" ? dateLoading :
    cancelLoading;

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header + Tabs card */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", boxShadow: "0 4px 20px rgba(4,52,58,0.3)", mb: 3, overflow: "hidden" }}>
        {/* Title row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: "20px 24px 16px" }}>
          <AutorenewIcon sx={{ color: "#fff", fontSize: 28 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px", lineHeight: 1.2 }}>
              Autopay Payment Records
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "12px", mt: "2px" }}>
              Razorpay subscription autopay transactions
            </Typography>
          </Box>
        </Box>

        {/* Tab bar */}
        <Box sx={{ display: "flex", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab;
            const count = tab === "All" ? allTotal : 0;
            return (
              <Box
                key={tab}
                onClick={() => { setActiveTab(tab); localStorage.setItem("autopayTab", tab); }}
                sx={{
                  px: "24px", py: "12px", cursor: "pointer", position: "relative",
                  display: "flex", alignItems: "center", gap: "8px",
                  color: isActive ? "#fff" : "rgba(255,255,255,0.5)",
                  fontWeight: isActive ? 600 : 400, fontSize: "14px",
                  letterSpacing: "0.2px", userSelect: "none", transition: "color 0.15s",
                  "&:hover": { color: "#fff" },
                  "&::after": isActive ? {
                    content: '""', position: "absolute", bottom: 0, left: 0, right: 0,
                    height: "3px", background: "#1db954", borderRadius: "3px 3px 0 0",
                  } : {},
                }}
              >
                {tab}
                {count > 0 && (
                  <Box component="span" sx={{
                    background: isActive ? "rgba(29,185,84,0.25)" : "rgba(255,255,255,0.12)",
                    color: isActive ? "#1db954" : "rgba(255,255,255,0.5)",
                    fontSize: "11px", fontWeight: 700, borderRadius: "20px",
                    px: "7px", py: "1px", lineHeight: 1.6,
                  }}>
                    {count}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Table card */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#016551" }} />
          </Box>
        ) : activeTab === "All" ? (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { background: "#f8fafc", fontWeight: 700, fontSize: "11px", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap", py: "10px" } }}>
                    <TableCell>S.No</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Subscription ID</TableCell>
                    <TableCell>Payment ID</TableCell>
                    <TableCell>Amount (₹)</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allData.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: "#94a3b8" }}>No records found</TableCell></TableRow>
                  ) : allData.map((row, i) => (
                    <TableRow key={row.id || i} sx={{ "&:hover": { background: "#f0f7ff" }, "&:nth-of-type(even)": { background: "#f8fafc" }, "& td": { fontSize: "12px", py: "8px", borderBottom: "1px solid #f1f5f9" } }}>
                      <TableCell sx={{ color: "#94a3b8" }}>{(allPage - 1) * PAGE_SIZE + i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>{row.userid__username || "-"}</TableCell>
                      <TableCell sx={{ color: "#475569" }}>{row.userid__phoneno || "-"}</TableCell>
                      <TableCell sx={{ color: "#64748b", fontFamily: "monospace", fontSize: "11px" }}>{row.razorpay_subscription_id || "-"}</TableCell>
                      <TableCell sx={{ color: "#64748b", fontFamily: "monospace", fontSize: "11px" }}>{row.payment_id || "-"}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#016551" }}>
                        ₹{Number(row.amount).toLocaleString("en-IN", { minimumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>{fmtDate(row.payment_date)}</TableCell>
                      <TableCell>
                        <Chip label={row.payment_status ? "Success" : "Failed"} size="small" sx={{
                          height: 20, fontSize: "10px", fontWeight: 700,
                          background: row.payment_status ? "rgba(22,163,74,0.1)" : "rgba(239,68,68,0.1)",
                          color: row.payment_status ? "#16a34a" : "#ef4444",
                          border: `1px solid ${row.payment_status ? "#bbf7d0" : "#fecaca"}`,
                        }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {allTotal > PAGE_SIZE && (
              <PaginationControls totalCount={allTotal} page={allPage} setPage={setAllPage} offset={PAGE_SIZE} />
            )}
          </>
        ) : activeTab === "Date-wise" ? (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& th": { background: "#f8fafc", fontWeight: 700, fontSize: "11px", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0", py: "10px" } }}>
                    <TableCell>S.No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Payments Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dateData.length === 0 ? (
                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 6, color: "#94a3b8" }}>No data found</TableCell></TableRow>
                  ) : dateData.map((row, i) => (
                    <TableRow key={row.date} sx={{ "&:hover": { background: "#f0f7ff" }, "&:nth-of-type(even)": { background: "#f8fafc" }, "& td": { fontSize: "13px", py: "10px", borderBottom: "1px solid #f1f5f9" } }}>
                      <TableCell sx={{ color: "#94a3b8", width: 60 }}>{(datePage - 1) * PAGE_SIZE + i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>{row.date}</TableCell>
                      <TableCell>
                        <Chip label={row.count} size="small" sx={{ height: 22, fontSize: "12px", fontWeight: 700, background: "rgba(1,101,81,0.1)", color: "#016551", border: "1px solid #bbf7d0", minWidth: 40 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {dateTotal > 0 && (
              <PaginationControls totalCount={dateTotal} page={datePage} setPage={setDatePage} offset={PAGE_SIZE} />
            )}
          </>
        ) : (
          /* ── Cancel Records ─────────────────────────────────────────── */
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { background: "#f8fafc", fontWeight: 700, fontSize: "11px", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap", py: "10px" } }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>User Name</TableCell>
                  <TableCell>User Email</TableCell>
                  <TableCell>Cancelled By</TableCell>
                  <TableCell>Subscription ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cancel at Cycle End</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Cancel Reason</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cancelData.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: "#94a3b8" }}>No cancel records found</TableCell></TableRow>
                ) : cancelData.map((row, i) => (
                  <TableRow key={row.id} sx={{ "&:hover": { background: "#f0f7ff" }, "&:nth-of-type(even)": { background: "#f8fafc" }, "& td": { fontSize: "12px", py: "8px", borderBottom: "1px solid #f1f5f9" } }}>
                    <TableCell sx={{ color: "#94a3b8" }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>{row.subscribed_user_name || "-"}</TableCell>
                    <TableCell sx={{ color: "#475569" }}>{row.subscribed_user_email || "-"}</TableCell>
                    <TableCell sx={{ color: "#475569" }}>{row.cancel_by_name || "-"}</TableCell>
                    <TableCell sx={{ color: "#64748b", fontFamily: "monospace", fontSize: "11px" }}>{row.subscription_id || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.current_status || "-"}
                        size="small"
                        sx={{
                          height: 20, fontSize: "10px", fontWeight: 700,
                          background: row.current_status === "cancelled" ? "rgba(239,68,68,0.1)" : "rgba(1,101,81,0.1)",
                          color: row.current_status === "cancelled" ? "#ef4444" : "#016551",
                          border: `1px solid ${row.current_status === "cancelled" ? "#fecaca" : "#bbf7d0"}`,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.cancel_at_cycle_end ? "Yes" : "No"}
                        size="small"
                        sx={{
                          height: 20, fontSize: "10px", fontWeight: 700,
                          background: row.cancel_at_cycle_end ? "rgba(234,179,8,0.1)" : "rgba(100,116,139,0.1)",
                          color: row.cancel_at_cycle_end ? "#ca8a04" : "#64748b",
                          border: `1px solid ${row.cancel_at_cycle_end ? "#fef08a" : "#e2e8f0"}`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#475569" }}>{fmtDate(row.valid_until)}</TableCell>
                    <TableCell sx={{ color: "#64748b", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.cancel_reason || row.cancellation_reason || "-"}
                    </TableCell>
                    <TableCell sx={{ color: "#475569" }}>{fmtDate(row.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
